/*=== CORE IMPORTS ===*/
import { html, LitElement } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { q1ProjectActivationPanelStyles } from './q1-project-activation-panel-styles.js';

/*=== LIBRARY COMPONENTS ===*/
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/drawer/drawer.js';
import '@shoelace-style/shoelace/dist/components/details/details.js';
import '@shoelace-style/shoelace/dist/components/divider/divider.js';
import '@shoelace-style/shoelace/dist/components/progress-bar/progress-bar.js';

/*=== Q1 MODULES ===*/
import {
  projectActivationStatusMap,
  projectActivationStatusOptions,
} from '../../models/project-client-enums.js';

/*=== COMPONENT CLASS ===*/

export class Q1ProjectActivationPanel extends LitElement {
  static styles = [ q1ProjectActivationPanelStyles ];

  static properties = {    
    
    /**
     * The project object to display. Must include all of the organizer / admin fields.
     */
    project: { type: Object },

  };

  constructor() {
    super();

    this.project = null;
  }

  /*=== ACTIONS ===*/

  show() {
    this.renderRoot.getElementById('drawer').show();
  }

  hide() {
    this.renderRoot.getElementById('drawer').hide();
  }

  /*=== GETTERS ===*/

  get isEmpty() {
    return !this.project || !this.project.key;
  }

  get displayName() {
    return this.project?.name || 'Unknown Project';
  }

  get projectLink() {
    return `/projects/${this.project?.key}`;
  }

  get activationStatus() {
    return this.project?.activationStatus || 'created';
  }

  /*=== TEMPLATES ===*/

  render() {
    return html`
      <sl-drawer id="drawer" part="drawer" label="Project Activation Flow">
        ${this.introTextTemplate}

        <sl-divider></sl-divider>

        <h3>Activation Steps:</h3>

        ${this.activationStatusTemplate_created}
        ${this.activationStatusTemplate_published}
        ${this.activationStatusTemplate_activated}
        ${this.activationStatusTemplate_launched}

        <sl-divider></sl-divider>

        ${this.activationStatusTemplate_retired}

        ${this.footerTemplate}
      </sl-drawer>
    `;
  }

  get introTextTemplate() {
    return html`
      <p class="subtitle">
        The checklists below will guide you through the project activation flow and help you 
        build a network of support around your project.
      </p>

      <p class="small">
        As your project reaches activation milestones, additional abilities and features are 
        unlocked. This flow culminates with an optional "launch" supported directly by the 
        Q1 Foundation team promoting your project and helping support your community goals.
      </p>
    `;
  }

  get footerTemplate() {
    return html`
      <button-bar slot="footer">
        <sl-button variant="primary" @click=${this.hide}>Close</sl-button>
      </button-bar>
    `;
  }

  get activationStatusTemplate_created() {
    return this._statusDetails('created', [
      {
        itemLabel: 'Write project headline',
        itemTooltip: "The project headline is a short description of your project that helps set "
         + "context for network members and prospective project members.",
        isChecked: this.project?.headline?.length || false,
        actionLabel: 'Project Details',
        actionPath: `${this.projectLink}/manage/details`,
      },
      {
        itemLabel: 'Write project description',
        itemTooltip: "The project description is a longer description of your project that can be "
          + "used to help set context for network members and prospective project members.",
        isChecked: this.project?.description?.length || false,
        actionLabel: 'Project Details',
        actionPath: `${this.projectLink}/manage/details`,
      },
      {
        itemLabel: 'Publish project',
        itemTooltip: "Once you've added a headline & description you can publish the project, which "
          + "allows you to start inviting members (organizers, contributors & supporters).",
        isChecked: ['published', 'activated', 'launched'].includes(this.activationStatus),
        actionLabel: 'Project Settings',
        actionPath: `${this.projectLink}/manage/settings`,
      },
    ]);
  }

  get activationStatusTemplate_published() {
    const memberCount = this.project?.memberCount || 1;
    const logCount = this.project?.logCount || 0;

    return this._statusDetails('published', [
      {
        itemLabel: 'Onboard 2 members',
        itemTooltip: "This requirement is satisfied when your member count reaches 2.",
        isChecked: memberCount >= 2,
        progressMax: 2,
        progressValue: memberCount,
        progressLabel: memberCount,
        actionLabel: 'Invite Members',
        actionPath: `${this.projectLink}/invite`,
      },
      {
        itemLabel: 'Post 3 log updates',
        itemTooltip: "This requirement is satisfied when your log item count reaches 3.",
        isChecked: logCount >= 3,
        progressMax: 3,
        progressValue: logCount,
        progressLabel: logCount,
        actionLabel: 'Post Update',
        actionPath: `${this.projectLink}/post`,
      },
    ]);
  }

  get activationStatusTemplate_activated() {
    const isLaunched = ['launched', 'retired'].includes(this.activationStatus);

    return this._statusDetails('activated', [
      {
        itemLabel: 'Claim custom URL',
        itemTooltip: "Select a custom project key, used in the URL and in other places.",
        isChecked: false, // FIXME hardcoded false for now
        actionLabel: 'Project Settings',
        actionPath: `${this.projectLink}/manage/settings`,
      },
      {
        itemLabel: 'Launch project',
        itemTooltip: "This step is optional but recommended if you're project would benefit from "
          + "increased visibility. Work with the Q1 Foundation team to "
          + "formally launch your project. This includes launch events, social media promotion, and "
          + "other support to help you reach your community goals.",
        isChecked: isLaunched,
      },
    ]);
  }

  get activationStatusTemplate_launched() {
    return this._statusDetails('launched', []);
  }

  /** This one is different since it isn't part of the main flow. */
  get activationStatusTemplate_retired() {
    return html`
      <p class="small">
        <strong>Notes About Project Retirement:</strong><br/><br/>
        Projects can be retired at any time by the organizers. Retirement (also called 'archiving')
        is a way to indicate that the project is no longer active. 
        Retired projects are still available to old members, but have limited interaction. 
        Projects can be re-published / unarchived at any time.
      </p>
    `;
  }

  /*=== UTILITY METHODS ===*/

  /** Returns true if the current activation status is PAST the target activation status. */
  _isCompleted(targetActivationStatus) {
    const activationStatusValues = projectActivationStatusOptions.map(s => s.value);
    const indexOfTarget = activationStatusValues.indexOf(targetActivationStatus);
    const indexOfCurrent = activationStatusValues.indexOf(this.activationStatus);
    return indexOfCurrent > indexOfTarget;
  }

  /** Returns true if the current activation status is EQUAL to the target activation status. */
  _isCurrent(targetActivationStatus) {
    return this.activationStatus === targetActivationStatus;
  }

  /** Returns the 1-based number of this status in the activation status options enum. */
  _statusNumber(targetActivationStatus) {
    const activationStatusValues = projectActivationStatusOptions.map(s => s.value);
    const indexOfTarget = activationStatusValues.indexOf(targetActivationStatus);
    return indexOfTarget + 1;
  }
  
  /**
   * Builds the html of the status sl-details.
   * Pass an html template into `contentTemplate` to add content to the details.
   */
  _statusDetails(activationStatus, checklistItems = []) {
    const settings = projectActivationStatusMap[activationStatus];
    if (!settings) return html`<span slot="summary">Unknown: ${activationStatus}</span>`;
    
    const statusNumber = this._statusNumber(activationStatus);
    const isCompleted = this._isCompleted(activationStatus);
    const isCurrent = this._isCurrent(activationStatus);

    return html`
      <sl-details
        ?open=${isCurrent}
        class=${classMap({
          completed: isCompleted,
          current: isCurrent,
        })}
      >
        
        <span slot="summary" class=${classMap({
          'status-summary': true,
          completed: isCompleted,
          current: isCurrent,
        })}>
          <span class="number">${statusNumber}</span>
          <span class="label">${settings.label}</span>

          ${!isCurrent ? '' : html`
            <sl-icon name="flag" library="material" class="suffix"></sl-icon>
          `}
          ${!isCompleted ? '' : html`
            <sl-icon name="check" library="material" class="suffix"></sl-icon>
          `}
        </span>

        ${!settings.organizerNotes ? '' : html`
          <span class="description">${settings.organizerNotes}</span>
        `}

        ${!checklistItems.length ? '' : html`
          <div class="checklist">
            ${checklistItems.map(item => this._checklistItem(item))}
          </div>
        `}

      </sl-details>
    `;
  }

  /**
   * Builds the html of a checklist item. Action anchor is only shown if the item is not checked.
   */
  _checklistItem({
    itemLabel, itemTooltip, isChecked,
    progressMax, progressValue, progressLabel: progressLabel_raw,
    actionLabel, actionPath,
  }) {
    const progressLabel = isChecked
      ? html`<sl-icon name="check" library="material"></sl-icon>`
      : progressValue || '';
    
    return html`
      <div class=${classMap({
        'checklist-item': true,
        checked: isChecked,
      })}>
        <sl-icon
          library="material"
          name=${isChecked ? 'check_box' : 'check_box_outline_blank'}
        ></sl-icon>
        <span class="label">
          ${itemLabel}
          ${isChecked || !itemTooltip ? '' : html`
            <sl-tooltip content=${itemTooltip} placement="top">
              <sl-icon name="info" library="material"></sl-icon>
            </sl-tooltip>
          `}
        </span>
        ${isChecked || !actionPath ? '' : html`
          <sl-button size="small" href=${actionPath} outline>
            <sl-icon name="arrow_forward" library="material" slot="prefix"></sl-icon>
            ${actionLabel}
          </sl-button>
        `}

        ${!progressMax ? '' : html`
          <sl-progress-bar
            value=${Math.min(progressValue / progressMax * 100, 100)}
          >${progressLabel}</sl-progress-bar>
        `}
      </div>
    `;
  }

}

customElements.define('q1-project-activation-panel', Q1ProjectActivationPanel);
